import useRoutineCompletions from "./useRoutineCompletions";
import React, { useEffect, useState } from "react";
import MyPresetList from "./MyPresetList";
import CreatePreset from "./MyPresetUtils/CreatePreset";
import EditPreset from "./MyPresetUtils/EditPreset";
import useMyPresetSync from "./useMyPresetSync";
import { Alert } from "@mui/material";
import DownloadPresetBackup from "./MyPresetUtils/DownloadPresetBackup.js";
import { handleSignOut } from "../utils/utilFunctions";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import { DAILY_PRESETS_FILTER_STORAGE_KEY } from "../HomePage/usePreferences";

const PresetManager = (props) => {
  const { onFlowChange } = props;
  const sync = useMyPresetSync(props.user.uid);
  const completions = useRoutineCompletions(props.user.uid);
  const [dailyOnly, setDailyOnly] = useState(() => {
    try {
      return localStorage.getItem(DAILY_PRESETS_FILTER_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(DAILY_PRESETS_FILTER_STORAGE_KEY, String(dailyOnly));
    } catch {
      // Filtering still works for this session when storage is unavailable.
    }
  }, [dailyOnly]);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const statusText = sync.status === "synced"
    ? "Last confirmed synced. Click to re-sync now."
    : sync.status === "saving"
      ? "Fetching or saving presets…"
      : sync.error;

  const addPreset = (presetName, presetValue, isDailyRoutine = false) => {
    if (sync.save({ presetName, presetTasks: presetValue, isDailyRoutine })) {
      setIsCreating(false);
    }
  };

  const updatePreset = (presetId, newPresetName, newTasks, isDailyRoutine = false) => {
    // Use the version that the editor opened, so a refresh during editing does
    // not hide a conflict with a newer backend version.
    if (sync.save({ ...editingPreset, id: presetId, presetName: newPresetName, presetTasks: newTasks, isDailyRoutine }, editingPreset)) {
      setIsEditing(false);
      setEditingPreset(null);
    }
  };

  const handleEditPreset = (preset) => {
    setEditingPreset(preset);
    setIsEditing(true);
  };

  const capitalizeName = (name) => {
    if (!name) return "";
    return (
      name.split(" ")[0].charAt(0).toUpperCase() +
      name.split(" ")[0].slice(1).toLowerCase()
    );
  };

  const isSubFlow = isCreating || isEditing;

  useEffect(() => {
    onFlowChange?.(isSubFlow);

    return () => {
      onFlowChange?.(false);
    };
  }, [isSubFlow, onFlowChange]);

  return (
    <div
      className={`my-presets-manager ${
        isSubFlow ? "my-presets-manager-flow" : ""
      }`}
    >
      {isCreating ? (
        <CreatePreset
          user={props.user}
          addPreset={addPreset}
          cancelCreate={() => setIsCreating(false)}
          mainTasks={props.tasks}
        />
      ) : isEditing ? (
        <EditPreset
          editingPreset={editingPreset}
          updatePreset={updatePreset}
          cancelEdit={() => {
            setIsEditing(false);
            setEditingPreset(null);
          }}
          mainTasks={props.tasks}
        />
      ) : (
        <>
          <header className="my-presets-header">
            <div className="my-presets-heading">
              <span className="my-presets-heading-icon">
                <BookmarkRoundedIcon />
              </span>
              <div>
                <h2>My presets</h2>
                <p>
                  {capitalizeName(props.user?.displayName) || "Friend"}'s saved
                  routines
                </p>
              </div>
            </div>

            <button
              className="my-presets-create-button"
              onClick={() => setIsCreating(true)}
            >
              <AddRoundedIcon />
              <span>New preset</span>
            </button>
          </header>

          <div className="my-presets-list-panel">
            <MyPresetList
              presets={sync.presets}
              onReorder={sync.reorder}
              completions={completions}
              loading={sync.status === "saving" && sync.presets.length === 0}
              onDelete={(preset) => sync.save(null, preset)}
              dailyOnly={dailyOnly}
              startCreating={() => setIsCreating(true)}
              addTasksToList={props.addTasksToList}
              setIsMenuOn={props.setIsMenuOn}
              handleEdit={handleEditPreset}
            />
          </div>

          <p className="my-presets-storage-note">
            {sync.status === "synced"
              ? "Saved locally and synced. Checks on open every 30 minutes; re-sync anytime."
              : sync.status === "saving"
                ? "Saved locally. Checking the backend…"
                : "Sync unavailable. Locally saved presets are still usable."}
          </p>

          <footer className="my-presets-footer">
            <div className="my-presets-footer-tools">
              <DownloadPresetBackup presets={sync.presets} />
              <button
                type="button"
                className="my-presets-daily-toggle"
                aria-label="Only daily routine presets"
                aria-pressed={dailyOnly}
                title={dailyOnly ? "Show all presets" : "Show daily routines only"}
                onClick={() => setDailyOnly((value) => !value)}
              >
                <RepeatRoundedIcon />
                <span>Daily</span>
              </button>
            </div>
            <button
              type="button"
              className="my-presets-sync-button"
              title={statusText}
              aria-label={statusText}
              aria-busy={sync.status === "saving"}
              onClick={sync.retry}
            >
              <span className={`my-presets-sync-dot my-presets-sync-dot-${sync.status}`} />
              <SyncRoundedIcon fontSize="small" />
              <span className="my-presets-sync-label" role="status" aria-live="polite">
                {sync.status === "synced" ? "Synced" : sync.status === "saving" ? "Syncing" : "Retry sync"}
              </span>
            </button>
            <button
              className="my-presets-footer-button my-presets-signout-button"
              onClick={() => handleSignOut(props.onSignOut)}
            >
              <LogoutRoundedIcon />
              <span>Sign out</span>
            </button>
          </footer>
        </>
      )}
      {sync.error && <Alert severity="error" className="my-presets-sync-error">{sync.error}</Alert>}
    </div>
  );
};

export default PresetManager;
